import {
  Badge,
  Button,
  Card,
  DataTable,
  Pagination,
  TextStyle,
} from "@shopify/polaris";
import React from "react";

const ImportHistoryTable = ({
  importHistories,
  pagination,
  onImportClick,
  onPageChange,
  getStatusBadge,
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "-";
    const sizes = ["Bytes", "KB", "MB", "GB"];
    if (bytes === 0) return "0 Bytes";
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
  };

  const getStatusTone = (status) => {
    const statusConfig = {
      completed: "success",
      failed: "critical",
      processing: "warning",
      pending: "subdued",
      rolled_back: "info",
    };
    return statusConfig[status] || "subdued";
  };

  if (importHistories.length === 0) {
    return (
      <Card>
        <Card.Section>
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <TextStyle variation="subdued">No import history found</TextStyle>
          </div>
        </Card.Section>
      </Card>
    );
  }

  const rows = importHistories.map((importHistory) => [
    <div>
      <TextStyle variation="strong">{importHistory.filename}</TextStyle>
      <br />
      <TextStyle variation="subdued">
        {formatFileSize(importHistory.file_size)}
      </TextStyle>
      {importHistory.gcp_file_id && (
        <>
          <br />
          <TextStyle variation="subdued" style={{ fontSize: '0.85em' }}>
            GCP: {importHistory.gcp_file_id}
          </TextStyle>
        </>
      )}
    </div>,
    <div>
      <Badge status={getStatusTone(importHistory.status)}>
        {getStatusBadge(importHistory.status)}
      </Badge>
      {importHistory.has_errors && (
        <Badge status="warning" style={{ marginLeft: "0.5rem" }}>
          {importHistory.error_count} errors
        </Badge>
      )}
    </div>,
    formatDate(importHistory.started_at),
    importHistory.duration_formatted || "-",
    <div>
      <TextStyle variation="strong">
        {importHistory.total_products_processed}
      </TextStyle>{" "}
      total
      <br />
      <TextStyle variation="subdued">
        {importHistory.products_created} created,{" "}
        {importHistory.products_updated} updated
      </TextStyle>
    </div>,
    <div>
      <Badge status="info" style={{ marginRight: "0.25rem" }}>
        {importHistory.change_summary?.price_changes || 0} prices
      </Badge>
      <Badge status="warning" style={{ marginRight: "0.25rem" }}>
        {importHistory.change_summary?.inventory_changes || 0} inventory
      </Badge>
      {importHistory.change_summary?.promotional_changes > 0 && (
        <Badge status="success">
          {importHistory.change_summary.promotional_changes} promo
        </Badge>
      )}
    </div>,
    <div>
      <Button size="slim" onClick={() => onImportClick(importHistory)}>
        View Details
      </Button>
      {importHistory.can_rollback && (
        <Button
          size="slim"
          tone="critical"
          style={{ marginLeft: "0.25rem" }}
          onClick={(e) => {
            e.stopPropagation();
            onImportClick(importHistory);
          }}
        >
          Rollback
        </Button>
      )}
    </div>,
  ]);

  const headers = [
    "File",
    "Status",
    "Started",
    "Duration",
    "Products",
    "Success Rate",
    "Changes",
    "Actions",
  ];

  return (
    <div>
      <Card>
        <Card.Section>
          <DataTable
            columnContentTypes={[
              "text",
              "text",
              "text",
              "text",
              "text",
              "text",
              "text",
              "text",
            ]}
            headings={headers}
            rows={rows}
            hoverable
          />
        </Card.Section>
      </Card>

      {pagination.total_pages > 1 && (
        <div style={{ marginTop: "1rem", textAlign: "center" }}>
          <Pagination
            hasPrevious={pagination.current_page > 1}
            onPrevious={() => onPageChange(pagination.current_page - 1)}
            hasNext={pagination.current_page < pagination.total_pages}
            onNext={() => onPageChange(pagination.current_page + 1)}
            label={`Page ${pagination.current_page} of ${pagination.total_pages}`}
          />
        </div>
      )}
    </div>
  );
};

export default ImportHistoryTable;
